/**
 * Format a number as Nigerian Naira currency
 */
export function formatNaira(value: number, compact = false): string {
  if (compact) {
    if (value >= 1e12) {
      return `₦${(value / 1e12).toFixed(1)}T`;
    }
    if (value >= 1e9) {
      return `₦${(value / 1e9).toFixed(1)}B`;
    }
    if (value >= 1e6) {
      return `₦${(value / 1e6).toFixed(1)}M`;
    }
    if (value >= 1e3) {
      return `₦${(value / 1e3).toFixed(1)}K`;
    }
  }
  
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a number with thousand separators
 */
export function formatNumber(value: number, decimals = 0): string {
  return new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a percentage
 */
export function formatPercent(value: number, decimals = 1): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(decimals)}%`;
}

/**
 * Format a percentage change with color indicator
 */
export function getPercentChangeColor(value: number): string {
  if (value > 0) return 'text-success-400';
  if (value < 0) return 'text-danger-400';
  return 'text-surface-400';
}

/**
 * Format rank with ordinal suffix
 */
export function formatRank(rank: number): string {
  const j = rank % 10;
  const k = rank % 100;
  
  if (j === 1 && k !== 11) {
    return rank + 'st';
  }
  if (j === 2 && k !== 12) {
    return rank + 'nd';
  }
  if (j === 3 && k !== 13) {
    return rank + 'rd';
  }
  return rank + 'th';
}

/**
 * Parse a number from formatted string (handles commas, spaces, quotes)
 */
export function parseFormattedNumber(value: string): number {
  // Remove quotes, spaces, and commas
  const cleaned = value.replace(/["'\s,]/g, '').trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Get month index from month name
 */
export function getMonthIndex(monthName: string): number {
  const months: Record<string, number> = {
    'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
    'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
  };
  return months[monthName] ?? 0;
}

/**
 * Parse column header to extract month and year
 */
export function parseColumnHeader(header: string): { month: string; year: number } | null {
  const match = header.match(/^([A-Za-z]{3})-(\d{4})$/);
  if (!match) return null;
  return { month: match[1], year: parseInt(match[2], 10) };
}

/**
 * Create a date from month-year string
 */
export function monthYearToDate(monthYear: string): Date {
  const parsed = parseColumnHeader(monthYear);
  if (!parsed) return new Date();
  return new Date(parsed.year, getMonthIndex(parsed.month), 1);
}

/**
 * Abbreviate state name for compact display
 */
export function abbreviateState(state: string): string {
  const abbreviations: Record<string, string> = {
    'Akwa Ibom': 'A.Ibom',
    'Cross River': 'C.River',
    'Federal Capital Territory': 'FCT',
  };
  return abbreviations[state] || state;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '…';
}

